import type { CSSProperties } from 'react';
import type { Space } from './spaces.js';
import { useWallpaper } from './wallpaper.js';
import type { Atmosphere } from './atmosphere.js';

export interface SceneSpec {
  /** dawn / mist / night are Home's atmospheres; hue is a module's own scene; photo is yours. */
  kind: 'dawn' | 'mist' | 'night' | 'hue' | 'photo';
  mode: 'light' | 'dark';
  hue: number;
  key: string;
}

export function useSceneFor(space: Space | null, atmosphere: Atmosphere): SceneSpec {
  const wallpaper = useWallpaper();
  if (!space || space.id === 'home') {
    if (wallpaper.choice === 'photo' && wallpaper.photo) return { kind: 'photo', mode: wallpaper.photoMode, hue: 0, key: 'photo' };
    const kind = atmosphere === 'deep' ? 'night' : atmosphere === 'fog' ? 'mist' : 'dawn';
    return {
      kind,
      mode: kind === 'night' ? 'dark' : 'light',
      hue: 262,
      key: kind,
    };
  }
  return {
    kind: 'hue',
    mode: space.mode,
    hue: space.hue,
    key: `${space.id}:${space.hue}:${space.mode}`,
  };
}

/**
 * The backdrop of a space: a gradient sky, three slow lights, a horizon and film grain.
 * Dark scenes add a lit planet rim and faint stars. Purely decorative.
 */
export function Scene({ spec, style, className }: { spec: SceneSpec; style?: CSSProperties; className?: string }) {
  const photo = useWallpaper((s) => s.photo);
  return (
    <div
      aria-hidden
      className={`nw-scene ${className ?? ''}`}
      data-scene={spec.kind}
      data-mode={spec.mode}
      style={{
        ['--h' as string]: spec.hue,
        ...(spec.kind === 'photo' && photo ? { backgroundImage: `url("${photo}")` } : {}),
        ...style,
      }}
    >
      {spec.kind === 'photo' ? (
        <span className="nw-scene-veil" />
      ) : (
        <>
          <span className="nw-scene-light nw-scene-a" />
          <span className="nw-scene-light nw-scene-b" />
          <span className="nw-scene-light nw-scene-c" />
          {spec.mode === 'dark' ? <span className="nw-scene-stars" /> : null}
          <span className="nw-scene-horizon" />
        </>
      )}
      <span className="nw-scene-grain" />
    </div>
  );
}
